import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Select,
  Button,
  message,
  Modal,
  Form,
  Input,
  Row,
  Col,
  Spin,
  Typography,
  Descriptions,
} from "antd";
import { UploadOutlined, FileSearchOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import apiClient from '../../services/apiClient';
import "./AdminPages.css";

const { Option } = Select;

/**
 * Admin tool for managing termsheet universe data.
 * Mirrors IssuerFinancialReportsAdmin pattern with:
 *  - Issuer + ISIN selection
 *  - View + edit UniverseTermsheetEntity fields
 *  - PDF upload + AI extraction via REACT_APP_BOTS_API_URL
 *  - Side‑by‑side comparison before saving.
 */
const TermsheetAdmin = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8080";
  const botsApiUrl =
    process.env.REACT_APP_BOTS_API_URL ||
    process.env.REACT_APP_POC_BOTS_URL ||
    "http://localhost:5002";
  const token = localStorage.getItem("token");

  const [issuerOptions, setIssuerOptions] = useState([]);
  const [selectedIssuer, setSelectedIssuer] = useState(null);
  const [isinOptions, setIsinOptions] = useState([]);
  const [selectedIsin, setSelectedIsin] = useState(null);

  const [termsheet, setTermsheet] = useState(null);
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [comparisonModalVisible, setComparisonModalVisible] = useState(false);
  const [newTermsheetModalVisible, setNewTermsheetModalVisible] = useState(false);
  const [extractionLoading, setExtractionLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [editableExtractedData, setEditableExtractedData] = useState(null);

  const authHeaders = useMemo(() => {
    const headers = { "Content-Type": "application/json" };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  useEffect(() => {
    fetchIssuerOptions();
  }, []);

  useEffect(() => {
    if (selectedIssuer?.issuer) {
      fetchIsinOptions(selectedIssuer.issuer);
    } else {
      setIsinOptions([]);
      setSelectedIsin(null);
      setTermsheet(null);
    }
  }, [selectedIssuer]);

  useEffect(() => {
    if (selectedIsin) {
      fetchTermsheet(selectedIsin);
    } else {
      setTermsheet(null);
    }
  }, [selectedIsin]);

  const fetchIssuerOptions = async () => {
    try {
      const response = await apiClient.get(
        `${baseUrl}/master-issuer/issuers/basic-industry`,
        { headers: authHeaders }
      );
      const result = response.data;
      if (result.statusCode === 200 && Array.isArray(result.data)) {
        setIssuerOptions(result.data);
      } else {
        setIssuerOptions([]);
      }
    } catch (err) {
      console.error("Error fetching issuer options:", err);
      message.error("Failed to load issuer list");
      setIssuerOptions([]);
    }
  };

  const fetchIsinOptions = async (issuerName) => {
    try {
      const url = new URL(`${baseUrl}/master-isin/isin`);
      url.searchParams.append("issuerName", issuerName);
      const response = await apiClient.get(url.toString(), { headers: authHeaders });
      if (response.status === 204 || response.status === 404) {
        setIsinOptions([]);
        setSelectedIsin(null);
        return;
      }
      const result = response.data;
      const list = Array.isArray(result.data) ? result.data : [];
      setIsinOptions(
        list.map((item) => ({
          isin: item.isin,
          label: `${item.isin} - ${item.companyIssuerName || ""}`.trim(),
        }))
      );
      if (!selectedIsin && list.length > 0) {
        setSelectedIsin(list[0].isin);
      }
    } catch (err) {
      console.error("Error fetching ISIN options:", err);
      message.error("Failed to load ISIN list");
      setIsinOptions([]);
      setSelectedIsin(null);
    }
  };

  const fetchTermsheet = async (isin) => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `${baseUrl}/termsheet/${encodeURIComponent(isin)}`,
        { headers: authHeaders }
      );
      if (response.status === 404 || response.status === 204) {
        setTermsheet(null);
        return;
      }
      const result = response.data;
      setTermsheet(result.data || null);
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status === 204) {
        setTermsheet(null);
      } else {
        console.error("Error fetching termsheet:", err);
        message.error(err.message || "Failed to load termsheet");
        setTermsheet(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const closeModals = () => {
    setComparisonModalVisible(false);
    setNewTermsheetModalVisible(false);
    setExtractionLoading(false);
    setExtractedData(null);
    setEditableExtractedData(null);
    form.resetFields();
  };

  const handleSave = async () => {
    if (!selectedIsin) {
      message.warning("Please select an ISIN");
      return;
    }
    try {
      const values = await form.validateFields();
      const payload = {
        ...termsheet,
        ...values,
        isin: selectedIsin,
      };

      const response = await apiClient.put(
        `${baseUrl}/termsheet/${encodeURIComponent(selectedIsin)}`,
        payload,
        {
          headers: authHeaders,
        }
      );
      const result = response.data;
      setTermsheet(result.data || payload);
      message.success("Termsheet updated successfully");
      closeModals();
    } catch (err) {
      if (err?.errorFields) return;
      console.error("Error updating termsheet:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data ||
        err.message;
      message.error(errMsg || "Failed to update termsheet");
    }
  };

  const handleDelete = () => {
    if (!selectedIsin) {
      message.warning("Please select an ISIN to delete its termsheet");
      return;
    }

    Modal.confirm({
      title: "Delete Termsheet",
      content: `Are you sure you want to delete the termsheet for ISIN ${selectedIsin}?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await apiClient.delete(
            `${baseUrl}/termsheet/${encodeURIComponent(selectedIsin)}`,
            {
              headers: authHeaders,
            }
          );
          if (response.status >= 200 && response.status < 300) {
            message.success("Termsheet deleted successfully");
          }
          setTermsheet(null);
        } catch (err) {
          console.error("Error deleting termsheet:", err);
          const errMsg =
            err.response?.data?.message ||
            err.response?.data ||
            err.message;
          message.error(errMsg || "Failed to delete termsheet");
        }
      },
    });
  };

  const handleUploadAndExtractPDF = () => {
    if (!selectedIsin) {
      message.warning("Please select an ISIN before uploading PDF");
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setExtractionLoading(true);
      const hasExistingTermsheet = termsheet !== null && termsheet !== undefined;
      
      // Only show comparison modal if termsheet data already exists
      if (hasExistingTermsheet) {
        setComparisonModalVisible(true);
      }
      setExtractedData(null);

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("isin", selectedIsin);

        const response = await fetch(`${botsApiUrl}/api/extract-termsheet`, {
          method: "POST",
          body: formData,
        });

        const result = await response.json();
        if (result.status === "success" && result.data) {
          // Expect Python to return keys aligned with UniverseTermsheetEntity fields (camelCase).
          setExtractedData(result.data);
          setEditableExtractedData(result.data);
          
          if (hasExistingTermsheet) {
            // Show comparison modal for existing termsheet
            message.success("Termsheet data extracted successfully. Please review and compare.");
          } else {
            // No existing termsheet - show editable modal with all fields
            setNewTermsheetModalVisible(true);
            message.success("Termsheet data extracted successfully. Please review and save.");
          }
        } else {
          message.error(result.message || "Failed to extract termsheet data");
          if (hasExistingTermsheet) {
            setComparisonModalVisible(false);
          } else {
            setNewTermsheetModalVisible(false);
          }
        }
      } catch (err) {
        console.error("Error extracting termsheet:", err);
        message.error(err.message || "Failed to extract termsheet data");
        if (hasExistingTermsheet) {
          setComparisonModalVisible(false);
        } else {
          setNewTermsheetModalVisible(false);
        }
      } finally {
        setExtractionLoading(false);
      }
    };
    input.click();
  };

  const handleSaveNewTermsheet = async () => {
    if (!selectedIsin || !editableExtractedData) return;
    
    try {
      const payload = {
        ...editableExtractedData,
        isin: selectedIsin,
      };
      
      // Use POST endpoint for saving new termsheet (handles both create and update)
      const response = await apiClient.post(
        `${baseUrl}/termsheet/${encodeURIComponent(selectedIsin)}`,
        payload,
        {
          headers: authHeaders,
        }
      );
      
      const result = response.data;
      setTermsheet(result.data || payload);
      message.success(result.message || "Termsheet data saved successfully");
      closeModals();
      // Refresh the termsheet data
      await fetchTermsheet(selectedIsin);
    } catch (err) {
      console.error("Error saving extracted termsheet:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data ||
        err.message;
      message.error(errMsg || "Failed to save termsheet data");
    }
  };

  const handleApplyExtracted = async () => {
    if (!selectedIsin || !editableExtractedData) return;
    try {
      const payload = {
        ...(termsheet || {}),
        ...editableExtractedData,
        isin: selectedIsin,
      };
      const response = await apiClient.put(
        `${baseUrl}/termsheet/${encodeURIComponent(selectedIsin)}`,
        payload,
        {
          headers: authHeaders,
        }
      );
      const result = response.data;
      setTermsheet(result.data || payload);
      message.success("Extracted termsheet data saved successfully");
      closeModals();
    } catch (err) {
      console.error("Error saving extracted termsheet:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data ||
        err.message;
      message.error(errMsg || "Failed to save extracted termsheet");
    }
  };

  const handleUpdateExtractedField = (fieldKey, value) => {
    setEditableExtractedData((prev) => ({
      ...(prev || {}),
      [fieldKey]: value,
    }));
  };

  const areValuesDifferent = (val1, val2) => {
    const a =
      val1 === null || val1 === undefined ? "" : String(val1).trim();
    const b =
      val2 === null || val2 === undefined ? "" : String(val2).trim();
    return a !== b;
  };

  // Helper to render a readable label from a camelCase or snake_case field name
  const getLabelFromField = (field) => {
    if (!field) return "";
    const withSpaces = field
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .toLowerCase();
    return withSpaces
      .split(" ")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  return (
    <div className="admin-page">
      <div className="admin-header-banner">
        <div className="hero-content">
          <h1 className="hero-title-news">Termsheet Management</h1>
        </div>
      </div>

      <Card className="admin-main-card">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="bottom">
          <Col xs={24} md={8}>
            <label className="admin-filter-label">Issuer</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select issuer"
              value={selectedIssuer?.issuer}
              onChange={(value) => {
                const issuer = issuerOptions.find((item) => item.issuer === value);
                setSelectedIssuer(issuer || null);
              }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {issuerOptions.map((issuer) => (
                <Option key={issuer.issuer} value={issuer.issuer}>
                  {issuer.issuer}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <label className="admin-filter-label">ISIN</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select ISIN"
              value={selectedIsin}
              onChange={setSelectedIsin}
              showSearch
              optionFilterProp="children"
              disabled={!selectedIssuer}
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {isinOptions.map((item) => (
                <Option key={item.isin} value={item.isin}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col
            xs={24}
            md={8}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
              gap: 8,
            }}
          >
            <Button
              type="text"
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => {
                if (!selectedIsin) {
                  message.warning("Please select an ISIN to edit");
                  return;
                }
                navigate(
                  `/admin/manage-termsheets/edit/${encodeURIComponent(
                    selectedIsin
                  )}`
                );
              }}
              disabled={!termsheet || !selectedIsin}
              title="Edit Termsheet"
            />
            <Button
              type="text"
              shape="circle"
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              disabled={!termsheet}
              title="Delete Termsheet"
            />
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleUploadAndExtractPDF}
              disabled={!selectedIsin}
              loading={extractionLoading}
            >
              Upload Termsheet PDF
            </Button>
          </Col>
        </Row>

        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spin size="large" />
          </div>
        ) : !termsheet ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <FileSearchOutlined style={{ fontSize: 32, marginBottom: 8 }} />
            <p>No termsheet data found for the selected ISIN.</p>
          </div>
        ) : (
          // Read-only details styled similar to InstrumentDetails termsheet section
          <Descriptions
            bordered
            column={1}
            size="small"
            labelStyle={{
              fontWeight: 600,
              width: 280,
            }}
            contentStyle={{
              wordBreak: "break-word",
            }}
          >
            {Object.keys(termsheet)
              .filter(
                (field) =>
                  field !== "createdAt" &&
                  field !== "updatedAt" &&
                  field !== "masterISIN"
              )
              .map((field) => (
                <Descriptions.Item
                  key={field}
                  label={getLabelFromField(field)}
                >
                  {termsheet[field] != null && termsheet[field] !== ""
                    ? Array.isArray(termsheet[field])
                      ? termsheet[field].join(", ")
                      : String(termsheet[field])
                    : "N/A"}
                </Descriptions.Item>
              ))}
          </Descriptions>
        )}
      </Card>

      <Modal
        open={comparisonModalVisible}
        onCancel={closeModals}
        title="Compare & Update Termsheet"
        width={1000}
        okText="Save Changes"
        onOk={handleApplyExtracted}
        okButtonProps={{
          disabled: extractionLoading || !editableExtractedData,
        }}
      >
        <Spin
          spinning={extractionLoading}
          tip="Extracting termsheet data from PDF..."
        >
          {!editableExtractedData ? (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <p>Extracting termsheet data from PDF...</p>
            </div>
          ) : !termsheet ? (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <p>No existing termsheet data found. The extracted data will be saved directly.</p>
            </div>
          ) : (
            (() => {
              const excludedFields = [
                "createdAt",
                "updatedAt",
                "masterISIN",
              ];
              const allFields = Array.from(
                new Set([
                  ...Object.keys(termsheet || {}),
                  ...Object.keys(editableExtractedData || {}),
                ])
              ).filter((field) => !excludedFields.includes(field));

              const tableData = allFields.map((field) => {
                const currentValue =
                  termsheet && Object.prototype.hasOwnProperty.call(termsheet, field)
                    ? termsheet[field]
                    : null;
                const extractedValue =
                  editableExtractedData &&
                  Object.prototype.hasOwnProperty.call(
                    editableExtractedData,
                    field
                  )
                    ? editableExtractedData[field]
                    : null;
                const isDifferent = areValuesDifferent(
                  currentValue,
                  extractedValue
                );

                return {
                  key: field,
                  fieldKey: field,
                  field: getLabelFromField(field),
                  current: currentValue,
                  extracted: extractedValue,
                  isDifferent,
                };
              });

              return (
                <Table
                  className="issuer-financial-reports-table"
                  dataSource={tableData}
                  rowClassName={(record) =>
                    record.isDifferent ? "comparison-row-different" : ""
                  }
                  columns={[
                    {
                      title: "Field",
                      dataIndex: "field",
                      key: "field",
                      width: "30%",
                    },
                    {
                      title: "Current Data",
                      dataIndex: "current",
                      key: "current",
                      width: "30%",
                      render: (value) => {
                        if (value === null || value === undefined || value === "") {
                          return <span style={{ color: "#999" }}>-</span>;
                        }
                        const displayValue = Array.isArray(value)
                          ? value.join(", ")
                          : String(value);
                        return (
                          <span
                            style={{
                              padding: "4px 8px",
                              backgroundColor: "#f0f0f0",
                              borderRadius: "4px",
                              display: "inline-block",
                            }}
                          >
                            {displayValue}
                          </span>
                        );
                      },
                    },
                    {
                      title: "Extracted Data (Editable)",
                      dataIndex: "extracted",
                      key: "extracted",
                      width: "40%",
                      render: (value, record) => {
                        const fieldKey = record.fieldKey;
                        const displayValue =
                          value === null || value === undefined
                            ? ""
                            : Array.isArray(value)
                            ? value.join(", ")
                            : String(value);
                        return (
                          <Input.TextArea
                            value={displayValue}
                            onChange={(e) =>
                              handleUpdateExtractedField(
                                fieldKey,
                                e.target.value
                              )
                            }
                            autoSize={{ minRows: 1, maxRows: 4 }}
                            placeholder="Enter value"
                          />
                        );
                      },
                    },
                  ]}
                  pagination={false}
                  size="small"
                />
              );
            })()
          )}
        </Spin>
      </Modal>

      {/* Modal for new termsheet (no existing data) */}
      <Modal
        open={newTermsheetModalVisible}
        onCancel={closeModals}
        title="Review & Save Termsheet Data"
        width={1000}
        okText="Save Termsheet"
        onOk={handleSaveNewTermsheet}
        okButtonProps={{
          disabled: extractionLoading || !editableExtractedData,
        }}
      >
        <Spin
          spinning={extractionLoading}
          tip="Extracting termsheet data from PDF..."
        >
          {!editableExtractedData ? (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <p>Extracting termsheet data from PDF...</p>
            </div>
          ) : (
            (() => {
              const excludedFields = [
                "createdAt",
                "updatedAt",
                "masterISIN",
                "isin",
              ];
              const allFields = Object.keys(editableExtractedData || {}).filter(
                (field) => !excludedFields.includes(field)
              );

              const tableData = allFields.map((field) => {
                const extractedValue =
                  editableExtractedData &&
                  Object.prototype.hasOwnProperty.call(
                    editableExtractedData,
                    field
                  )
                    ? editableExtractedData[field]
                    : null;

                return {
                  key: field,
                  fieldKey: field,
                  field: getLabelFromField(field),
                  extracted: extractedValue,
                };
              });

              return (
                <Table
                  className="issuer-financial-reports-table"
                  dataSource={tableData}
                  columns={[
                    {
                      title: "Field",
                      dataIndex: "field",
                      key: "field",
                      width: "30%",
                    },
                    {
                      title: "Value (Editable)",
                      dataIndex: "extracted",
                      key: "extracted",
                      width: "70%",
                      render: (value, record) => {
                        const fieldKey = record.fieldKey;
                        const displayValue =
                          value === null || value === undefined
                            ? ""
                            : Array.isArray(value)
                            ? value.join(", ")
                            : String(value);
                        return (
                          <Input.TextArea
                            value={displayValue}
                            onChange={(e) =>
                              handleUpdateExtractedField(
                                fieldKey,
                                e.target.value
                              )
                            }
                            autoSize={{ minRows: 1, maxRows: 4 }}
                            placeholder="Enter value"
                          />
                        );
                      },
                    },
                  ]}
                  pagination={false}
                  size="small"
                />
              );
            })()
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default TermsheetAdmin;


